"use client";

/**
 * Shared "site card" preview — the evidence line under a URL input that
 * shows what's actually at the address BEFORE a run is charged.
 *
 * One implementation for both entry points (the marketing hero form and the
 * signed-in /audit/new configurator) so the two cards can never drift apart.
 * Backed by GET /api/preflight (structural check + DNS existence + a guarded
 * identity probe + "did you mean …?" — see api/preflight.ts). Everything here
 * is ADVISORY: verdicts change copy and may arm a caller-owned "are you
 * sure?" step; they never hard-block a run.
 *
 * Why it exists: DNS-only previews catch unregistered typos (stripesz.com)
 * but wave through REGISTERED typosquats — airdnb.com (2026-07-14) resolves
 * fine, serves an empty shell, and burned a run. Only showing the user the
 * evidence ("empty page", the real title, the redirect target, "did you mean
 * airbnb.com?") catches every wrong-destination class, including typos of
 * sites no curated list will ever contain.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../lib/api";
import { validateAuditUrl } from "../lib/guest-audit";
import { isOurOwnDomain } from "../lib/self-domain";
import { track } from "../lib/track";

export type PreviewVerdict =
  | "ok" // something real answered (title shown when we have one)
  | "noexist" // DNS: definite NXDOMAIN
  | "empty" // answered, nothing there — parked/typosquat/expired shells
  | "error-page" // answered with a 4xx error page at this exact URL
  | "unreachable" // resolved but nothing answered
  | "redirect"; // lands on a different host (informative, not a warning)

export type SitePreview =
  | { status: "idle" }
  | {
      status: "ready";
      hostname: string;
      verdict: PreviewVerdict;
      title?: string;
      finalHost?: string;
      didYouMean?: string;
    };

// Verdicts that should arm a submit-time "are you sure?" step. Redirects
// aren't warnings (rebrands and www-moves are normal); a did-you-mean
// near-miss is warn-level even when the page itself looks fine — a typosquat
// can serve a perfectly real-looking page.
export function isWarnPreview(p: SitePreview): boolean {
  if (p.status !== "ready") return false;
  if (p.didYouMean) return true;
  return (
    p.verdict === "empty" ||
    p.verdict === "error-page" ||
    p.verdict === "unreachable"
  );
}

// ---------------------------------------------------------------------------
// Shared fetch — one preflight per URL string, shared between the debounced
// typing preview AND the submit-time check, so a fast click never pays for a
// second request (and usually finds the debounce's request already in
// flight). Settled verdicts are memoized briefly; "unreachable" and failures
// are NOT (they're often transient — a retry should really retry).
// ---------------------------------------------------------------------------

const previewMemo = new Map<string, { p: Promise<SitePreview>; at: number }>();
const PREVIEW_MEMO_TTL_MS = 5 * 60 * 1000;
const PREVIEW_MEMO_MAX = 50;

export function fetchSitePreview(trimmedUrl: string): Promise<SitePreview> {
  const memod = previewMemo.get(trimmedUrl);
  if (memod && Date.now() - memod.at < PREVIEW_MEMO_TTL_MS) return memod.p;

  const p = (async (): Promise<SitePreview> => {
    try {
      const res = await fetch(
        `${API_BASE}/preflight?url=${encodeURIComponent(trimmedUrl)}`
      );
      if (!res.ok) return { status: "idle" };
      const data = (await res.json()) as {
        ok?: boolean;
        hostname?: string;
        exists?: "yes" | "no" | "unknown";
        didYouMean?: string;
        probe?: {
          status?: "ok" | "empty" | "unreachable" | "blocked" | "skipped";
          title?: string;
          finalHost?: string;
          httpStatus?: number;
        };
      };
      if (!data.ok || !data.hostname) return { status: "idle" };

      // Collapse the response into one card verdict. Priority: NXDOMAIN >
      // nothing-there warnings > cross-host redirect > confirmed identity.
      // "blocked"/"skipped" carry no evidence — render like plain "ok".
      let verdict: PreviewVerdict = "ok";
      if (data.exists === "no") {
        verdict = "noexist";
      } else if (data.probe?.status === "empty") {
        verdict = (data.probe.httpStatus ?? 0) >= 400 ? "error-page" : "empty";
      } else if (data.probe?.status === "unreachable") {
        verdict = "unreachable";
      } else if (data.probe?.finalHost) {
        verdict = "redirect";
      }
      return {
        status: "ready",
        hostname: data.hostname,
        verdict,
        title: data.probe?.title,
        finalHost: data.probe?.finalHost,
        didYouMean: data.didYouMean,
      };
    } catch {
      // Network hiccup — advisory, stay quiet.
      return { status: "idle" };
    }
  })();

  if (previewMemo.size >= PREVIEW_MEMO_MAX) {
    const oldest = previewMemo.keys().next().value;
    if (oldest !== undefined) previewMemo.delete(oldest);
  }
  previewMemo.set(trimmedUrl, { p, at: Date.now() });
  // Drop transient outcomes from the memo once known, so retries retry.
  void p.then((result) => {
    if (
      result.status === "idle" ||
      (result.status === "ready" && result.verdict === "unreachable")
    ) {
      if (previewMemo.get(trimmedUrl)?.p === p) previewMemo.delete(trimmedUrl);
    }
  });
  return p;
}

export interface SitePreflight {
  preview: SitePreview;
  /**
   * Submit-time check: resolve a verdict for `trimmedUrl` NOW, waiting up to
   * `budgetMs` (then fail-open with "idle" — a slow probe must never block a
   * real site). Shares the typing preview's request when one is in flight,
   * and pushes the result into the visible card so the user sees WHY they're
   * being asked. Callers race this against nothing else — it self-caps.
   */
  ensure: (trimmedUrl: string, budgetMs?: number) => Promise<SitePreview>;
}

/**
 * Debounced advisory preflight for a URL input. Every failure path lands on
 * "idle" — a broken preview must never look like a broken site. `onInvalidate`
 * fires when the input starts naming a DIFFERENT host than the visible card
 * (callers use it to disarm their confirm step).
 */
export function useSitePreflight(
  url: string,
  surface: string,
  onInvalidate?: () => void
): SitePreflight {
  const [preview, setPreview] = useState<SitePreview>({ status: "idle" });
  // Track each warned hostname once per mount, so the funnel counts distinct
  // near-miss typos rather than debounce ticks.
  const warnedHosts = useRef<Set<string>>(new Set());

  const trackWarn = useCallback(
    (p: SitePreview) => {
      if (p.status !== "ready") return;
      if (!isWarnPreview(p) && p.verdict !== "noexist") return;
      if (warnedHosts.current.has(p.hostname)) return;
      warnedHosts.current.add(p.hostname);
      track("Guest URL Preview Warned", {
        surface,
        verdict: p.verdict,
        didYouMean: Boolean(p.didYouMean),
      });
    },
    [surface]
  );

  useEffect(() => {
    const trimmed = url.trim();
    const valid = trimmed ? validateAuditUrl(trimmed) : null;
    if (!valid?.ok || isOurOwnDomain(trimmed)) {
      setPreview({ status: "idle" });
      return;
    }
    const nextHost = valid.hostname;
    setPreview((p) => {
      if (p.status !== "idle" && p.hostname !== nextHost) {
        onInvalidate?.();
        return { status: "idle" };
      }
      return p;
    });
    let cancelled = false;
    const timer = setTimeout(async () => {
      const next = await fetchSitePreview(trimmed);
      if (cancelled || next.status !== "ready") return;
      setPreview(next);
      trackWarn(next);
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // onInvalidate is a caller-stable callback; url/surface drive the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, surface]);

  const ensure = useCallback(
    async (trimmedUrl: string, budgetMs = 4500): Promise<SitePreview> => {
      const budget = new Promise<SitePreview>((resolve) => {
        setTimeout(() => resolve({ status: "idle" }), budgetMs);
      });
      const result = await Promise.race([fetchSitePreview(trimmedUrl), budget]);
      if (result.status === "ready") {
        // Make the card show the evidence the caller is about to act on.
        setPreview(result);
        trackWarn(result);
      }
      return result;
    },
    [trackWarn]
  );

  return { preview, ensure };
}

/**
 * The card's one-line verdict. Every warn state names the typed host in bold
 * (highlight the URL when asking "are you sure?") and, when we can name the
 * fix, offers it as a one-click swap that fills the input — never auto-runs.
 */
export function SitePreviewMessage({
  preview,
  confirmArmed,
  onSwap,
}: {
  preview: Extract<SitePreview, { status: "ready" }>;
  confirmArmed: boolean;
  onSwap: (host: string) => void;
}) {
  const host = <strong>{preview.hostname}</strong>;
  const swapButton = (target: string, label?: string) => (
    <button
      type="button"
      className="lp-hero-form-suggest"
      onClick={() => onSwap(target)}
    >
      {label ?? `Did you mean ${target}?`}
    </button>
  );
  const areYouSure = confirmArmed ? <>Are you sure? </> : null;

  if (preview.verdict === "noexist") {
    return (
      <>
        We can&rsquo;t find {host} — check the address.
        {preview.didYouMean && <> {swapButton(preview.didYouMean)}</>}
      </>
    );
  }
  if (preview.verdict === "empty") {
    return (
      <>
        {areYouSure}
        {host} is showing an empty page — double-check the address.
        {preview.didYouMean && <> {swapButton(preview.didYouMean)}</>}
      </>
    );
  }
  if (preview.verdict === "error-page") {
    return (
      <>
        {areYouSure}
        {host} is showing an error page — double-check the address.
        {preview.didYouMean && <> {swapButton(preview.didYouMean)}</>}
      </>
    );
  }
  if (preview.verdict === "unreachable") {
    return (
      <>
        {areYouSure}We couldn&rsquo;t reach {host} — the site may be down, or
        the address may be mistyped.
        {preview.didYouMean && <> {swapButton(preview.didYouMean)}</>}
      </>
    );
  }
  if (preview.verdict === "redirect" && preview.finalHost) {
    return (
      <>
        {host} takes you to <strong>{preview.finalHost}</strong>
        {preview.title ? <> — &ldquo;{preview.title}&rdquo;</> : null}.{" "}
        {swapButton(preview.finalHost, `Audit ${preview.finalHost} instead?`)}
      </>
    );
  }
  // "ok" — confirmed identity when we have a title, plain hostname otherwise.
  return (
    <>
      You&rsquo;re auditing {host}
      {preview.title ? <> — &ldquo;{preview.title}&rdquo;</> : null}
      {preview.didYouMean && <> {swapButton(preview.didYouMean)}</>}
    </>
  );
}

/**
 * Favicon for the preview chip, fetched by the visitor's browser from
 * Google's favicon service (our preflight probe never proxies images).
 * Falls back to a neutral dot when the service is blocked (ad-blockers) or
 * the site has no icon.
 */
export function SiteFavicon({ hostname }: { hostname: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [hostname]);
  if (failed) return <span className="lp-hero-preview-dot" aria-hidden="true" />;
  return (
    // Third-party favicon endpoint: next/image gains nothing here and would
    // need a remote-pattern allowlist for Google's favicon host.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      className="lp-hero-preview-favicon"
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=32`}
      alt=""
      width={16}
      height={16}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}
